import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarRange, Loader2, LogOut, Send, X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Copilot } from "@/components/Copilot";
import { api } from "@/pages/portal/api";

const pretty = (s) => (s || "").replace(/_/g, " ");

const STATUS_COLORS = {
  live_partner: "bg-emerald-500", ready_for_deployment: "bg-aqua", declined: "bg-red-600", deferred: "bg-yellow-600",
};

export default function AdminPipeline() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [data, setData] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ state: "", season: "", status: "" });
  const [rank, setRank] = useState(false);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.state) params.set("state", filters.state);
    if (filters.season) params.set("season", filters.season);
    if (filters.status) params.set("status", filters.status);
    if (rank) params.set("sort", "readiness");
    api.get(`/admin/pipeline?${params}`).then(({ data }) => setData(data)).catch(() => toast.error("Load failed"));
    api.get("/admin/pipeline/calendar").then(({ data }) => setCalendar(data)).catch(() => {});
    api.get("/admin/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, [filters, rank]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/admin/pipeline/${id}`); setSel(data); }
    catch { toast.error("Could not load partner"); }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); load(); if (sel) openDetail(sel.pipeline_id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const states = useMemo(() => [...new Set((data?.partners || []).map((p) => p.state))].sort(), [data]);

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-pipeline">
      <Navbar />
      <div className="border-b border-black/[0.08] bg-white/80 pt-[92px] backdrop-blur">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-4">
          <div>
            <p className="eyebrow">XE Admin · Seasonal Partner Pipeline</p>
            <h1 className="font-display text-xl font-extrabold tracking-tight text-offwhite" data-testid="pipeline-admin-title">Future Deployment Pipeline</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="btn-ghost !px-4 !py-2 !text-xs" data-testid="pipeline-back-xe">Control Center</Link>
            <span className="hidden text-xs text-muted sm:block">{user?.email}</span>
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="pipeline-signout"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
        <nav className="container-x flex gap-1">
          {[["list", "Partners"], ["calendar", "Deployment Calendar"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`pipeline-view-${k}`}
              className={`border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${view === k ? "border-aqua text-offwhite" : "border-transparent text-muted hover:text-offwhite"}`}>
              {label}
            </button>
          ))}
        </nav>
      </div>

      <main className="container-x py-8 pb-24">
        {view === "list" && (
          <>
            <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="pipeline-filters">
              <select className="field !w-auto !py-2 !text-xs" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} data-testid="filter-state">
                <option value="">All states</option>
                {states.map((s) => <option key={s}>{s}</option>)}
              </select>
              <input className="field !w-44 !py-2 !text-xs" placeholder="Season (e.g. Spring 2027)" value={filters.season} onChange={(e) => setFilters({ ...filters, season: e.target.value })} data-testid="filter-season" />
              <select className="field !w-auto !py-2 !text-xs" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
                <option value="">All statuses</option>
                {(data?.statuses || []).map((s) => <option key={s} value={s}>{data.status_labels[s]}</option>)}
              </select>
              <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-muted">
                <input type="checkbox" className="h-4 w-4 accent-aqua" checked={rank} onChange={(e) => setRank(e.target.checked)} data-testid="filter-rank" />
                Rank by readiness
              </label>
            </div>

            {!data && <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-aqua" /></p>}
            <div className="space-y-3">
              {data?.partners?.length === 0 && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No pipeline partners match these filters.</p>}
              {data?.partners?.map((p) => (
                <button key={p.pipeline_id} onClick={() => openDetail(p.pipeline_id)}
                  className="card-line block w-full rounded-xl bg-white p-5 text-left transition-colors hover:border-aqua/40"
                  data-testid={`pipeline-row-${p.pipeline_id}`}>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-sm font-extrabold text-offwhite">{p.business_name}
                        {p.on_hold && <span className="ml-2 rounded-full bg-yellow-400/20 px-2 py-0.5 text-[9px] font-bold uppercase text-yellow-700">On hold</span>}
                      </p>
                      <p className="mt-0.5 text-xs text-muted">{p.contact_name} · {p.city_state} · {p.preferred_season} · window {p.earliest_deployment} → {p.latest_deployment}</p>
                    </div>
                    {p.tentative_vehicle_name && <span className="text-[11px] font-semibold text-muted">Tentative: {p.tentative_vehicle_name}</span>}
                    <span className="font-mono text-xs text-muted">{p.readiness_score}% ready</span>
                    <span className="rounded-full border border-aqua/40 bg-aqua/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-aqua" data-testid={`pipeline-status-${p.pipeline_id}`}>
                      {p.status_label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {view === "calendar" && <CalendarView entries={calendar} onOpen={openDetail} />}
      </main>

      {sel && <DetailDrawer p={sel} vehicles={vehicles} onClose={() => setSel(null)} act={act} />}
      <Copilot audience="admin" />
    </div>
  );
}

function CalendarView({ entries, onOpen }) {
  const byMonth = useMemo(() => {
    const m = {};
    entries.forEach((e) => {
      const key = (e.earliest_deployment || "TBD").slice(0, 7);
      (m[key] = m[key] || []).push(e);
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [entries]);

  return (
    <div className="space-y-6" data-testid="pipeline-calendar">
      {!byMonth.length && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No requested deployments.</p>}
      {byMonth.map(([month, list]) => (
        <div key={month} className="card-line rounded-xl bg-white p-5">
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite">
            <CalendarRange size={15} className="text-aqua" />
            {new Date(`${month}-01T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </h2>
          <div className="mt-3 space-y-2">
            {list.map((e) => (
              <button key={e.pipeline_id} onClick={() => onOpen(e.pipeline_id)}
                className="flex w-full flex-wrap items-center gap-3 rounded-lg border border-black/[0.08] p-3 text-left transition-colors hover:border-aqua/40"
                data-testid={`cal-${e.pipeline_id}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${STATUS_COLORS[e.status] || "bg-black/25"}`} />
                <span className="text-sm font-bold text-offwhite">{e.business_name}</span>
                <span className="font-mono text-xs text-muted">{e.earliest_deployment} → {e.latest_deployment}</span>
                <span className="text-xs text-muted">{e.preferred_season} · {e.state}</span>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-muted">{pretty(e.status)}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailDrawer({ p, vehicles, onClose, act }) {
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [docReq, setDocReq] = useState("");
  const missingAuth = useMemo(() => {
    const need = ["agreement_completed", "location_approved", "insurance_confirmed", "training_completed", "booking_systems_tested", "payment_workflow_tested"];
    return need.filter((k) => !p.checklist.find((c) => c.key === k)?.done);
  }, [p]);
  const daysOut = p.earliest_deployment ? Math.ceil((new Date(p.earliest_deployment) - new Date()) / 864e5) : null;
  const canAuthorize = !p.authorization && daysOut !== null && daysOut <= 14 && daysOut >= 0 && p.certification && !missingAuth.length;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" data-testid="pipeline-drawer">
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-ink p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">{p.pipeline_id}</p>
            <h2 className="font-display text-xl font-extrabold text-offwhite">{p.business_name}</h2>
            <p className="mt-1 text-xs text-muted">{p.contact_name} · {p.email} · {p.phone} · {p.city_state}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-offwhite" data-testid="drawer-close"><X size={20} /></button>
        </div>

        {/* actions */}
        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <select className="field !py-2 !text-xs" value={p.status}
            onChange={(e) => act(() => api.patch(`/admin/pipeline/${p.pipeline_id}`, { status: e.target.value }), "Status updated")}
            data-testid="drawer-status">
            {Object.entries(p.status_labels || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="field !py-2 !text-xs" value={p.tentative_vehicle_id || ""}
            onChange={(e) => e.target.value && act(() => api.patch(`/admin/pipeline/${p.pipeline_id}`, { tentative_vehicle_id: e.target.value }), "Tentative vehicle assigned")}
            data-testid="drawer-vehicle">
            <option value="">Assign tentative vehicle…</option>
            {vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.name}</option>)}
          </select>
          <button onClick={() => act(() => api.patch(`/admin/pipeline/${p.pipeline_id}`, { on_hold: !p.on_hold }), p.on_hold ? "Hold removed" : "Partner on hold")}
            className="btn-ghost !py-2 !text-xs" data-testid="drawer-hold">{p.on_hold ? "Remove hold" : "Place on hold"}</button>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <input className="field !py-2 !text-xs" defaultValue={p.earliest_deployment} type="date"
            onBlur={(e) => e.target.value !== p.earliest_deployment && act(() => api.patch(`/admin/pipeline/${p.pipeline_id}`, { earliest_deployment: e.target.value }), "Window updated")}
            data-testid="drawer-earliest" />
          <input className="field !py-2 !text-xs" defaultValue={p.latest_deployment} type="date"
            onBlur={(e) => e.target.value !== p.latest_deployment && act(() => api.patch(`/admin/pipeline/${p.pipeline_id}`, { latest_deployment: e.target.value }), "Window updated")}
            data-testid="drawer-latest" />
          <input className="field !py-2 !text-xs" defaultValue={p.assigned_rep} placeholder="Assigned rep"
            onBlur={(e) => e.target.value !== p.assigned_rep && act(() => api.patch(`/admin/pipeline/${p.pipeline_id}`, { assigned_rep: e.target.value }), "Rep assigned")}
            data-testid="drawer-rep" />
        </div>
        <p className="mt-2 text-[10px] text-muted">Tentative vehicle assignments never display as "Vehicle Reserved" and guarantee nothing.</p>

        {/* authorization */}
        <div className="mt-5 rounded-xl border border-black/[0.08] bg-white p-4" data-testid="drawer-authorization">
          <p className="font-display text-sm font-extrabold text-offwhite">Final deployment authorization</p>
          {p.authorization ? (
            <p className="mt-2 text-xs text-emerald-700">Authorized in writing by {p.authorization.authorized_by} on {p.authorization.at.slice(0, 10)} — {p.authorization.note}</p>
          ) : (
            <>
              <p className="mt-1 text-[11px] text-muted">
                Available within 14 days of earliest deployment ({daysOut} days out), after partner certification and required checklist items.
              </p>
              {missingAuth.length > 0 && <p className="mt-1 text-[11px] text-red-600">Missing: {missingAuth.map(pretty).join(", ")}</p>}
              {!p.certification && <p className="mt-1 text-[11px] text-red-600">Partner readiness certification missing</p>}
              <button
                disabled={!canAuthorize}
                onClick={() => act(() => api.post(`/admin/pipeline/${p.pipeline_id}/authorize-deployment`, { note: "Authorized by XE administrator" }), "Deployment authorized")}
                className="btn-primary mt-3 !py-2.5 !text-xs disabled:opacity-40" data-testid="drawer-authorize">
                Issue Final Authorization
              </button>
            </>
          )}
          {p.authorization && p.status !== "live_partner" && (
            <button onClick={() => act(() => api.post(`/admin/pipeline/${p.pipeline_id}/go-live`), "Partner is live")}
              className="btn-primary mt-3 !py-2.5 !text-xs" data-testid="drawer-go-live">Mark Live (Deployed)</button>
          )}
        </div>

        {/* checklist */}
        <div className="mt-5 rounded-xl border border-black/[0.08] bg-white p-4">
          <p className="font-display text-sm font-extrabold text-offwhite">Readiness checklist · {p.readiness_score}%</p>
          <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {p.checklist.map((c) => (
              <label key={c.key} className="flex cursor-pointer items-center gap-2 text-xs text-muted" data-testid={`drawer-check-${c.key}`}>
                <input type="checkbox" className="h-3.5 w-3.5 accent-aqua" checked={c.done}
                  onChange={(e) => act(() => api.post(`/admin/pipeline/${p.pipeline_id}/checklist/${c.key}`, { done: e.target.checked }))} />
                <span className={c.done ? "font-semibold text-offwhite" : ""}>{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* info dump */}
        <div className="mt-5 grid gap-3 rounded-xl border border-black/[0.08] bg-white p-4 text-xs sm:grid-cols-2">
          {[["Current services", p.current_services], ["Operating location", p.operating_location],
            ["Waterfront address", p.waterfront_address], ["Storage / dock / ramp", p.storage_dock_ramp],
            ["Staffing & training", p.staffing_training], ["Permits", p.permit_status],
            ["Insurance readiness", p.insurance_readiness], ["Insurance coverage", "Covered by Xtreme Exotics (XINSURANCE) — partner additional insured, deductible 45% capped at $2,500"], ["Booking & payment", p.booking_payment_system],
            ["Estimated ready", p.estimated_ready_date], ["Delay reasons", p.delay_reasons],
            ["Alternative dates", p.alternative_dates], ["Preferred Jet Car", `${p.preferred_jet_car} (not guaranteed)`]].map(([k, v]) => (
            <div key={k}><p className="font-mono text-[9px] uppercase tracking-widest text-muted">{k}</p><p className="mt-0.5 text-offwhite">{v || "—"}</p></div>
          ))}
        </div>

        {/* documents */}
        <div className="mt-5 rounded-xl border border-black/[0.08] bg-white p-4">
          <p className="font-display text-sm font-extrabold text-offwhite">Documents ({(p.documents || []).length})</p>
          <div className="mt-2 space-y-1">
            {(p.documents || []).map((d) => <p key={d.doc_id} className="text-xs text-muted">{d.label} · {d.filename} · {d.at.slice(0, 10)}</p>)}
          </div>
          <div className="mt-3 flex gap-2">
            <input className="field !py-2 !text-xs" placeholder="Request items, comma-separated (e.g. COI, permit)" value={docReq} onChange={(e) => setDocReq(e.target.value)} data-testid="drawer-docreq" />
            <button onClick={() => act(() => api.post(`/admin/pipeline/${p.pipeline_id}/request-documents`, { items: docReq.split(",").map((s) => s.trim()).filter(Boolean) }), "Documents requested").then(() => setDocReq(""))}
              className="btn-ghost !px-4 !py-2 !text-xs" data-testid="drawer-docreq-btn">Request</button>
          </div>
        </div>

        {/* notes */}
        <div className="mt-5 rounded-xl border border-black/[0.08] bg-white p-4">
          <p className="font-display text-sm font-extrabold text-offwhite">Internal notes</p>
          <div className="mt-2 space-y-1.5">
            {(p.notes || []).map((n) => <p key={n.note_id} className="text-xs text-muted"><b className="text-offwhite">{n.by}:</b> {n.note} <span className="font-mono text-[9px]">{n.at.slice(0, 10)}</span></p>)}
          </div>
          <div className="mt-3 flex gap-2">
            <input className="field !py-2 !text-xs" placeholder="Add internal note (partner never sees)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="drawer-note" />
            <button onClick={() => act(() => api.post(`/admin/pipeline/${p.pipeline_id}/notes`, { note }), "Note added").then(() => setNote(""))}
              className="btn-ghost !px-4 !py-2 !text-xs" data-testid="drawer-note-btn">Add</button>
          </div>
        </div>

        {/* messages */}
        <div className="mt-5 rounded-xl border border-black/[0.08] bg-white p-4">
          <p className="font-display text-sm font-extrabold text-offwhite">Messages</p>
          <div className="mt-2 max-h-48 space-y-1.5 overflow-y-auto">
            {(p.messages || []).map((m) => (
              <p key={m.message_id} className="text-xs text-muted"><b className="text-offwhite">{m.from === "partner" ? p.business_name : "XE"}:</b> {m.message} <span className="font-mono text-[9px]">{new Date(m.at).toLocaleDateString()}</span></p>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input className="field !py-2 !text-xs" placeholder="Message the partner (emailed too)" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="drawer-msg" />
            <button onClick={() => act(() => api.post(`/admin/pipeline/${p.pipeline_id}/messages`, { message: msg }), "Message sent").then(() => setMsg(""))}
              className="btn-ghost !px-4 !py-2 !text-xs" data-testid="drawer-msg-btn"><Send size={12} /></button>
          </div>
        </div>

        {/* audit */}
        <div className="mt-5 rounded-xl border border-black/[0.08] bg-white p-4" data-testid="drawer-audit">
          <p className="font-display text-sm font-extrabold text-offwhite">Audit history</p>
          <div className="mt-2 space-y-1">
            {[...(p.audit || [])].reverse().map((a, i) => (
              <p key={i} className="text-[11px] text-muted">
                <span className="font-mono">{new Date(a.at).toLocaleString()}</span> · <b className="text-offwhite">{pretty(a.action)}</b> · {a.actor}
                {a.meta?.to ? ` (${pretty(a.meta.from)} → ${pretty(a.meta.to)})` : ""}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
